import { z } from 'zod'

// Selling plan delivery settings schema
export const DeliverySettingsSchema = z.object({
    providerSpecific: z.record(
        z.string(),
        z.object({
            sellingPlanId: z.string(),
            sellingPlanGroupId: z.string().optional(),
            interval: z.enum(['day', 'week', 'month', 'year']),
            intervalCount: z.number(),
        }),
    ).optional(),
})

// Selling plan pricing policy schema
export const PricingPolicySchema = z.object({
    type: z.enum(['fixed', 'recurring']),
    adjustmentType: z.enum(['FIXED_AMOUNT', 'PERCENTAGE', 'PRICE']),
    adjustmentValue: z.number(),
    afterCycle: z.number().optional(),
})

// Selling plan price settings schema
export const PriceSettingsSchema = z.object({
    providerSpecific: z.record(
        z.string(),
        z.object({
            sellingPlanId: z.string(),
            sellingPlanGroupId: z.string().optional(),
            pricingPolicies: z.array(PricingPolicySchema).optional(),
        }),
    ).optional(),
})

// Price entity schema
export const PriceSchema = z.object({
    id: z.string(),
    amount: z.number().default(0),
    currency: z.string().optional(),
    recurring: z.boolean(),
    interval: z.string().nullable(),
    intervalCount: z.number().nullable(),
    default: z.boolean().nullable(),
    rebillMode: z.enum(['auth', 'capture']).nullable(),
    rebillStepIntervalMs: z.number().nullable(),
    rebillWithOrder: z.boolean().nullable(),
    currencyOptions: z.record(
        z.string(),
        z.object({
            amount: z.number(),
            rate: z.number().optional(),
            lock: z.boolean().optional(),
            lockedBy: z.enum(['merchant', 'sync']).optional(),
            date: z.string().optional(),
        }),
    ),
    /** The purchase option this recurring price materializes (Subscriptions V3); null on other prices. */
    purchaseOptionId: z.string().nullable().optional(),
    // Selling plan fields
    isExternalSellingPlan: z.boolean().nullable(),
    isInternalSellingPlan: z.boolean().nullable(),
    shopifySellingPlanId: z.string().nullable(),
    deliverySettings: DeliverySettingsSchema.nullable().optional(),
    priceSettings: PriceSettingsSchema.nullable().optional(),
    addDeliveryOnRebill: z.boolean().nullable().optional(),
})


export const TaxCategorySchema = z.object({
    id: z.string(),
    slug: z.string(),
    label: z.string(),
    taxjarCode: z.string().nullable(),
})

// Simple price info for list views
export const PriceInfoSchema = z.object({
    addDeliveryOnRebill: z.boolean().optional().default(false),
    recurring: z.boolean(),
    interval: z.string().nullable(),
    intervalCount: z.number().nullable(),
    billingTiming: z.string().nullable(),
    currencyOptions: z.record(z.string(), z.object({ amount: z.number() })).nullable(),
})

// Variant entity schema
export const VariantSchema = z.object({
    id: z.string(),
    name: z.string(),
    sku: z.string().nullable(),
    weight: z.number().nullable(),
    imageUrl: z.string().nullable(),
    default: z.boolean().nullable(),
    prices: z.array(PriceSchema),
})

/** Variant row returned by products.list when includeVariants is true. */
export const ProductListVariantSchema = z.object({
    id: z.string(),
    productId: z.string().optional(),
    name: z.string(),
    description: z.string().nullable(),
    sku: z.string().nullable(),
    externalVariantId: z.string().nullable().optional(),
    imageUrl: z.string().nullable(),
    default: z.boolean().nullable(),
    active: z.boolean().optional(),
    inStock: z.boolean().optional(),
    grams: z.number().optional(),
    creditPrice: z.number().nullable().optional(),
    price: z.number().nullable().optional(),
    currency: z.string().nullable().optional(),
    compareAtPrice: z.number().nullable().optional(),
    accountId: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    prices: z.array(PriceSchema),
})

// Simple product schema for list views
export const ProductSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    image: z.string().nullable(),
    active: z.boolean().nullable().optional(),
    isShippable: z.boolean().nullable().optional(),
    isTaxable: z.boolean().nullable().optional(),
    autoSync: z.boolean().nullable().optional(),
    unitLabel: z.string().nullable().optional(),
    accountingCode: z.string().nullable().optional(),
    taxCategory: z.string().nullable().optional(),
    mappedTaxCategory: TaxCategorySchema.nullable().optional(),
    price: PriceInfoSchema,
    variants: z.array(ProductListVariantSchema).optional(),
})

// Full product schema with variants and prices
export const ProductDetailsSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    image: z.string().nullable(),
    variants: z.array(VariantSchema),
    defaultPrice: PriceSchema.nullable(),
})


// Product with full relations (returned from DB queries)
export const ProductWithRelationsSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable().default('-'),
    active: z.boolean(),
    storeId: z.string(),
    accountId: z.string(),
    externalProductId: z.string().nullable(),
    unitLabel: z.string().nullable().default('-'),
    accountingCode: z.string().nullable().default('-'),
    taxCategory: z.string().nullable().default('-'),
    isShippable: z.boolean().nullable().default(false),
    isTaxable: z.boolean().nullable().default(false),
    autoSync: z.boolean().nullable().default(false),
    createdAt: z.date().nullable().default(new Date()),
    updatedAt: z.date().nullable().default(new Date()),
    mappedTaxCategory: TaxCategorySchema.nullable(),
    variants: z.array(
        z.object({
            id: z.string(),
            name: z.string(),
            description: z.string().nullable().default('-'),
            sku: z.string().nullable(),
            grams: z.number().nullable().default(0),
            imageUrl: z.string().nullable(),
            default: z.boolean().nullable().default(false),
            active: z.boolean().nullable().default(false),
            prices: z.array(PriceSchema),
            inStock: z.boolean(),
            creditPrice: z.number().nullable().optional(),
            /** Crossed-out price in minor units; already read by the query, kept on the way out. */
            compareAtPrice: z.number().nullable().optional(),
        }),
    ),
})

export type DeliverySettings = z.infer<typeof DeliverySettingsSchema>
export type PricingPolicy = z.infer<typeof PricingPolicySchema>
export type PriceSettings = z.infer<typeof PriceSettingsSchema>
export type Price = z.infer<typeof PriceSchema>
export type PriceInfo = z.infer<typeof PriceInfoSchema>
export type Variant = z.infer<typeof VariantSchema>
export type Product = z.infer<typeof ProductSchema>
export type ProductDetails = z.infer<typeof ProductDetailsSchema>
export type ProductWithRelations = z.infer<typeof ProductWithRelationsSchema>

